/* ============================================================================
   Myoform - "What We Test" directory data
   ----------------------------------------------------------------------------
   This file is the ONLY place insight content lives. The section markup
   (sections/dna-what-we-test.liquid) renders nothing per-insight; every card,
   search result and detail drawer is built from the arrays below.

   TO ADD AN INSIGHT
   Append an object to MyoformWhatWeTest.insights. Every field is a plain
   string. It will appear in search, in its category, in "All", and it will
   open in the same detail drawer with no extra HTML.

     {
       id: "creatine-response",          // unique, lowercase-hyphenated. Becomes the deep link (#creatine-response)
       category: "training",             // must match a categories[].id below
       name: "Creatine response",
       shortDescription: "One line for the directory card.",
       whatItIs: "...",                  // drawer: WHAT IT IS
       whyItMatters: "...",              // drawer: WHY IT MATTERS
       geneticInfluence: "...",          // drawer: WHAT YOUR GENETICS CAN INFLUENCE
       howMyoformUsesIt: "...",          // drawer: HOW MYOFORM USES IT
       keywords: "creatine atp power"    // optional, search-only synonyms
     }

   TO ADD A CATEGORY
   1. Append to MyoformWhatWeTest.categories with a unique id.
   2. Add a matching <symbol id="wwt-icon-{icon}"> to the SVG sprite in
      sections/dna-what-we-test.liquid.

   LANGUAGE RULES (please keep to these)
   - Never deterministic: use "may influence", "associated with",
     "genetic tendency", "can contribute to".
   - No disease, diagnosis or treatment claims.
   - No named SNPs or genes.
   ========================================================================== */

window.MyoformWhatWeTest = {
  categories: [
    {
      id: 'nutrients',
      label: 'Nutrients',
      icon: 'nutrients',
      description:
        'How your genetics may influence the way you absorb, convert and utilise vitamins, minerals and macronutrients.',
    },
    {
      id: 'training',
      label: 'Training',
      icon: 'training',
      description:
        'Traits associated with performance characteristics and how your body may respond to different training stimuli.',
    },
    {
      id: 'recovery',
      label: 'Recovery',
      icon: 'recovery',
      description:
        'Genetic tendencies associated with repair, inflammatory response and connective tissue resilience.',
    },
    {
      id: 'energy',
      label: 'Energy',
      icon: 'energy',
      description:
        'Pathways associated with fuel utilisation, metabolic efficiency and day-to-day energy production.',
    },
    {
      id: 'neuroperformance',
      label: 'Neuroperformance',
      icon: 'neuroperformance',
      description:
        'Traits associated with stress response, sleep, focus and motivation.',
    },
    {
      id: 'hydration',
      label: 'Hydration',
      icon: 'hydration',
      description:
        'Genetic tendencies associated with fluid balance, sodium response and electrolyte handling.',
    },
    {
      id: 'sensitivities',
      label: 'Sensitivities',
      icon: 'sensitivities',
      description:
        'How your body may process caffeine, alcohol, histamine and other dietary compounds.',
    },
  ],

  insights: [
    /* ---------------------------------------------------------------- NUTRIENTS */
    {
      id: 'vitamin-d-metabolism',
      category: 'nutrients',
      name: 'Vitamin D metabolism',
      shortDescription:
        'How your genetics may influence vitamin D production, transport and utilisation.',
      whatItIs:
        'Vitamin D plays an important role in muscle function, bone health, immune function and many other biological processes.',
      whyItMatters:
        'People can differ in how efficiently they produce, transport and utilise vitamin D, which is why the same sun exposure or dose can leave two people in very different positions.',
      geneticInfluence:
        'Genetic variants in vitamin D synthesis, transport and receptor pathways may contribute to differences in vitamin D status between individuals.',
      howMyoformUsesIt:
        'This insight can contribute to personalised nutrition and supplementation recommendations alongside your other genetic results.',
      keywords: 'vitamin d3 cholecalciferol sunlight bone immune',
    },
    {
      id: 'b-vitamin-metabolism',
      category: 'nutrients',
      name: 'B-vitamin metabolism',
      shortDescription:
        'Genetic differences affecting pathways involved in methylation, energy production and nutrient processing.',
      whatItIs:
        'The B vitamins act as cofactors across methylation, red blood cell formation and the pathways that release energy from food.',
      whyItMatters:
        'Because these vitamins work as a group, a tendency towards less efficient processing in one step can affect how well the wider pathway runs.',
      geneticInfluence:
        'Variants across B-vitamin transport and enzyme pathways are associated with differences in how efficiently these nutrients are converted into their active forms.',
      howMyoformUsesIt:
        'Where your profile suggests a higher requirement, we can prioritise active or pre-converted forms rather than simply raising the dose.',
      keywords: 'vitamin b complex methylation b1 b2 b3 b5 thiamine riboflavin niacin',
    },
    {
      id: 'folate-metabolism',
      category: 'nutrients',
      name: 'Folate metabolism',
      shortDescription: 'How efficiently your body may process and utilise folate.',
      whatItIs:
        'Folate is a B vitamin involved in methylation, DNA synthesis and the formation of new cells.',
      whyItMatters:
        'Folate has to be converted into an active form before your body can use it, and people can differ in how readily that conversion happens.',
      geneticInfluence:
        'Genetic variants in folate conversion and transport pathways are associated with differences in circulating folate and related markers.',
      howMyoformUsesIt:
        'This insight can inform whether an active folate form is preferred in your formula, and how it is balanced against the other B vitamins.',
      keywords: 'folate folic acid vitamin b9 methylfolate methylation',
    },
    {
      id: 'vitamin-b12-metabolism',
      category: 'nutrients',
      name: 'Vitamin B12 metabolism',
      shortDescription: 'Genetic factors involved in vitamin B12 transport and utilisation.',
      whatItIs:
        'Vitamin B12 supports red blood cell formation, nerve function and energy metabolism, and depends on a multi-step absorption and transport process.',
      whyItMatters:
        'Because B12 relies on carrier proteins to reach your tissues, transport efficiency can matter as much as intake.',
      geneticInfluence:
        'Variants in B12 binding and transport pathways are associated with differences in B12 status between individuals on similar diets.',
      howMyoformUsesIt:
        'This insight can contribute to the form and dose of B12 in your formula, considered alongside your diet and training load.',
      keywords: 'vitamin b12 cobalamin methylcobalamin vegan nerve',
    },
    {
      id: 'vitamin-b6-metabolism',
      category: 'nutrients',
      name: 'Vitamin B6 metabolism',
      shortDescription: 'Genetic differences in pathways involving vitamin B6.',
      whatItIs:
        'Vitamin B6 is a cofactor in protein metabolism, neurotransmitter production and haemoglobin formation.',
      whyItMatters:
        'B6 sits at the junction of amino acid and neurotransmitter pathways, so how well it is activated can have a broad effect.',
      geneticInfluence:
        'Genetic variants associated with B6 activation and turnover may contribute to differences in vitamin B6 status.',
      howMyoformUsesIt:
        'This insight can inform B6 dosing and form selection, and is read together with your protein and neuroperformance results.',
      keywords: 'vitamin b6 pyridoxine p5p neurotransmitter amino acid',
    },
    {
      id: 'iron-regulation',
      category: 'nutrients',
      name: 'Iron regulation',
      shortDescription:
        'Genetic factors associated with iron absorption, transport and utilisation.',
      whatItIs:
        'Iron is central to oxygen transport, aerobic energy production and endurance performance.',
      whyItMatters:
        'Iron is tightly regulated in both directions, so tendencies towards higher or lower absorption are both worth understanding before supplementing.',
      geneticInfluence:
        'Variants in iron absorption and transport pathways are associated with differences in iron storage and handling between individuals.',
      howMyoformUsesIt:
        'This insight helps us decide whether iron belongs in your formula at all, and is always considered alongside your own blood work.',
      keywords: 'iron ferritin haemoglobin hemoglobin anaemia oxygen endurance',
    },
    {
      id: 'magnesium',
      category: 'nutrients',
      name: 'Magnesium',
      shortDescription:
        'Traits associated with magnesium transport, utilisation and physiological demand.',
      whatItIs:
        'Magnesium is involved in hundreds of enzymatic reactions, including muscle contraction, nerve signalling and energy production.',
      whyItMatters:
        'Training, sweating and stress all increase magnesium demand, and individual handling of the mineral can differ.',
      geneticInfluence:
        'Genetic variants in magnesium transport pathways are associated with differences in magnesium status and requirement.',
      howMyoformUsesIt:
        'This insight can contribute to magnesium form and dose, and is read alongside your recovery, sleep and hydration results.',
      keywords: 'magnesium mineral cramp sleep muscle relaxation',
    },
    {
      id: 'calcium-metabolism',
      category: 'nutrients',
      name: 'Calcium metabolism',
      shortDescription:
        'Genetic differences associated with calcium regulation and utilisation.',
      whatItIs:
        'Calcium supports bone structure, muscle contraction and nerve signalling, and is closely regulated by hormonal pathways.',
      whyItMatters:
        'Calcium handling is linked to vitamin D and magnesium status, so it is best interpreted as part of a group rather than alone.',
      geneticInfluence:
        'Variants in calcium regulation and transport pathways may contribute to differences in how calcium is absorbed and used.',
      howMyoformUsesIt:
        'This insight is read together with your vitamin D and magnesium results to inform bone and muscle support in your plan.',
      keywords: 'calcium bone density skeletal mineral',
    },
    {
      id: 'zinc-metabolism',
      category: 'nutrients',
      name: 'Zinc metabolism',
      shortDescription:
        'Genetic factors involved in zinc transport and biological availability.',
      whatItIs:
        'Zinc supports immune function, protein synthesis, hormonal pathways and tissue repair.',
      whyItMatters:
        'Zinc competes with other minerals for absorption, so both requirement and timing can matter in a personalised formula.',
      geneticInfluence:
        'Genetic variants in zinc transport pathways are associated with differences in zinc availability between individuals.',
      howMyoformUsesIt:
        'This insight can inform zinc dosing and how it is separated from competing minerals in your formula.',
      keywords: 'zinc immune testosterone mineral repair',
    },
    {
      id: 'omega-3-metabolism',
      category: 'nutrients',
      name: 'Omega-3 metabolism',
      shortDescription:
        'How efficiently your body may convert and process essential fatty acids.',
      whatItIs:
        'Omega-3 fatty acids support cell membrane structure, inflammatory balance and cardiovascular and cognitive function.',
      whyItMatters:
        'Plant-based omega-3 has to be converted into its longer-chain forms, and conversion efficiency varies considerably between people.',
      geneticInfluence:
        'Variants in fatty acid conversion pathways are associated with differences in how readily shorter-chain omega-3 becomes EPA and DHA.',
      howMyoformUsesIt:
        'A lower conversion tendency can shift the recommendation towards pre-formed sources rather than a higher plant-based dose.',
      keywords: 'omega 3 epa dha fish oil fatty acid inflammation',
    },
    {
      id: 'antioxidant-capacity',
      category: 'nutrients',
      name: 'Antioxidant capacity',
      shortDescription:
        'Genetic differences in pathways involved in managing oxidative stress.',
      whatItIs:
        'Your antioxidant systems neutralise the reactive by-products produced by normal metabolism and hard training.',
      whyItMatters:
        'Training deliberately creates oxidative stress as an adaptation signal, so the goal is balance rather than maximum suppression.',
      geneticInfluence:
        'Variants in antioxidant enzyme pathways are associated with differences in how effectively oxidative by-products are managed.',
      howMyoformUsesIt:
        'This insight helps us time and dose antioxidant support so it aids recovery without blunting the adaptation you trained for.',
      keywords: 'antioxidant oxidative stress free radical glutathione vitamin c e',
    },
    {
      id: 'carbohydrate-metabolism',
      category: 'nutrients',
      name: 'Carbohydrate metabolism',
      shortDescription:
        'Genetic tendencies associated with carbohydrate processing and response.',
      whatItIs:
        'Carbohydrate metabolism covers how the carbohydrate you eat is digested, absorbed and moved into your cells.',
      whyItMatters:
        'Two people can respond quite differently to the same carbohydrate intake, which affects both fuelling and body composition strategies.',
      geneticInfluence:
        'Genetic variants associated with carbohydrate digestion and glucose handling may contribute to differences in carbohydrate response.',
      howMyoformUsesIt:
        'This insight can inform fuelling guidance and the carbohydrate strategy suggested around your training sessions.',
      keywords: 'carbohydrate carbs glucose starch amylase sugar',
    },
    {
      id: 'fat-metabolism',
      category: 'nutrients',
      name: 'Fat metabolism',
      shortDescription: 'How your genetics may influence fat metabolism and utilisation.',
      whatItIs:
        'Fat metabolism covers how dietary fat is transported, stored and made available as fuel.',
      whyItMatters:
        'Dietary fat response is one of the more individual areas of nutrition, which is why blanket advice often disappoints.',
      geneticInfluence:
        'Variants in lipid transport and processing pathways are associated with differences in how individuals respond to dietary fat.',
      howMyoformUsesIt:
        'This insight can shape the fat and fuelling guidance in your plan, alongside your energy system results.',
      keywords: 'fat lipid cholesterol triglyceride dietary fat saturated',
    },
    {
      id: 'protein-metabolism',
      category: 'nutrients',
      name: 'Protein metabolism',
      shortDescription:
        'Genetic factors involved in protein utilisation and amino acid metabolism.',
      whatItIs:
        'Protein metabolism covers how dietary protein is broken into amino acids and used for repair, adaptation and hormonal signalling.',
      whyItMatters:
        'Protein requirement is driven by training load and recovery demand, and genetic tendencies can shift where in the range you sit.',
      geneticInfluence:
        'Genetic variants in amino acid transport and turnover pathways may contribute to differences in protein utilisation.',
      howMyoformUsesIt:
        'This insight is read alongside your muscle development and recovery results to inform protein targets and amino acid support.',
      keywords: 'protein amino acid leucine bcaa nitrogen muscle',
    },

    /* ----------------------------------------------------------------- TRAINING */
    {
      id: 'power-vs-endurance',
      category: 'training',
      name: 'Power vs endurance',
      shortDescription:
        'Your genetic predisposition towards explosive or endurance-oriented performance characteristics.',
      whatItIs:
        'A summary of where your profile sits on the spectrum between explosive, short-duration output and sustained aerobic work.',
      whyItMatters:
        'Knowing your natural lean helps you decide what to build on and what needs deliberate work, rather than assuming both should feel equally easy.',
      geneticInfluence:
        'Variants associated with muscle fibre composition and energy system pathways may contribute to a predisposition towards one end of the spectrum.',
      howMyoformUsesIt:
        'This insight shapes the training guidance in your report and the emphasis of your formula between power and endurance support.',
      keywords: 'power endurance sprint explosive aerobic anaerobic athlete type',
    },
    {
      id: 'aerobic-capacity',
      category: 'training',
      name: 'Aerobic capacity',
      shortDescription:
        'Traits associated with oxygen utilisation and endurance performance.',
      whatItIs:
        'Aerobic capacity reflects how well your body takes in, transports and uses oxygen during sustained effort.',
      whyItMatters:
        'It is highly trainable, but your starting point and rate of change can differ, which is useful context for setting expectations.',
      geneticInfluence:
        'Genetic variants associated with oxygen transport and aerobic enzyme pathways may contribute to differences in aerobic capacity.',
      howMyoformUsesIt:
        'This insight informs endurance training guidance and the nutrients prioritised for oxygen transport and aerobic metabolism.',
      keywords: 'aerobic capacity vo2 endurance oxygen cardio stamina',
    },
    {
      id: 'vo2-response',
      category: 'training',
      name: 'VO2 response',
      shortDescription:
        'Genetic tendencies associated with improvements in aerobic fitness following training.',
      whatItIs:
        'This looks at trainability rather than starting level: how much your aerobic fitness may move in response to a given block of work.',
      whyItMatters:
        'Response to identical aerobic training varies widely, so understanding your tendency helps you judge whether a plan needs more time or a different stimulus.',
      geneticInfluence:
        'Variants associated with aerobic training response are linked to differences in the magnitude of improvement following structured training.',
      howMyoformUsesIt:
        'This insight can shape how your aerobic progression is paced and how recovery support is prioritised around it.',
      keywords: 'vo2 max trainability aerobic response endurance improvement',
    },
    {
      id: 'training-adaptation',
      category: 'training',
      name: 'Training adaptation',
      shortDescription:
        'How strongly your body may respond to different training stimuli.',
      whatItIs:
        'Adaptation is the process of your body remodelling itself in response to a repeated training signal.',
      whyItMatters:
        'If you tend to adapt slowly to one stimulus, more volume is not always the answer - often the stimulus itself needs changing.',
      geneticInfluence:
        'Genetic variants associated with adaptive signalling pathways may contribute to differences in response between training types.',
      howMyoformUsesIt:
        'This insight informs how your training guidance is periodised and which adaptations your formula is built to support.',
      keywords: 'adaptation responder trainability periodisation stimulus',
    },
    {
      id: 'muscle-development',
      category: 'training',
      name: 'Muscle development',
      shortDescription:
        'Genetic factors associated with muscle structure, strength and adaptation.',
      whatItIs:
        'This covers the traits associated with building and maintaining muscle tissue in response to resistance training.',
      whyItMatters:
        'Understanding your tendency helps set realistic timelines and points to where nutrition and recovery need the most attention.',
      geneticInfluence:
        'Variants in muscle structural and growth signalling pathways are associated with differences in muscle adaptation.',
      howMyoformUsesIt:
        'This insight is read with your protein and recovery results to inform training emphasis and amino acid support.',
      keywords: 'muscle hypertrophy lean mass growth strength building',
    },
    {
      id: 'muscle-fibre-characteristics',
      category: 'training',
      name: 'Muscle fibre characteristics',
      shortDescription:
        'Genetic variants associated with different muscle fibre characteristics.',
      whatItIs:
        'Skeletal muscle contains fibres with different contraction speeds and fatigue profiles, in proportions that vary between people.',
      whyItMatters:
        'Fibre profile influences which efforts feel natural and which need more deliberate development.',
      geneticInfluence:
        'Genetic variants associated with muscle fibre composition may contribute to differences in contractile characteristics.',
      howMyoformUsesIt:
        'This insight supports your power-versus-endurance interpretation and the training emphasis suggested in your report.',
      keywords: 'muscle fibre fiber fast twitch slow twitch type ii actn3',
    },
    {
      id: 'strength-potential',
      category: 'training',
      name: 'Strength potential',
      shortDescription: 'Traits associated with strength and power development.',
      whatItIs:
        'Strength potential describes the traits associated with producing high force and developing it further through training.',
      whyItMatters:
        'Strength underpins almost every other quality, so knowing your tendency helps you decide how much of your week it should occupy.',
      geneticInfluence:
        'Variants associated with muscle structure and neuromuscular signalling may contribute to differences in strength development.',
      howMyoformUsesIt:
        'This insight informs strength programming guidance and the performance ingredients prioritised in your formula.',
      keywords: 'strength power force maximal lifting neuromuscular',
    },
    {
      id: 'fatigue-response',
      category: 'training',
      name: 'Fatigue response',
      shortDescription:
        'How your biology may influence fatigue during intense or prolonged exercise.',
      whatItIs:
        'Fatigue response covers how quickly performance declines within a session and how that decline feels.',
      whyItMatters:
        'Recognising a genetic tendency towards earlier fatigue helps you distinguish a biological limiter from under-fuelling or poor pacing.',
      geneticInfluence:
        'Genetic variants associated with energy production and metabolic buffering pathways may contribute to differences in fatigue onset.',
      howMyoformUsesIt:
        'This insight can inform intra-session fuelling guidance and whether buffering or energy-support ingredients are prioritised.',
      keywords: 'fatigue tiredness endurance drop off buffering exhaustion',
    },
    {
      id: 'exercise-efficiency',
      category: 'training',
      name: 'Exercise efficiency',
      shortDescription:
        'Traits associated with how efficiently your body produces and uses energy during training.',
      whatItIs:
        'Exercise efficiency describes how much useful work you get from the energy you expend at a given intensity.',
      whyItMatters:
        'A more efficient profile can sustain the same pace for less metabolic cost, which changes how fuelling should be planned.',
      geneticInfluence:
        'Variants in energy production and mitochondrial pathways are associated with differences in metabolic efficiency during exercise.',
      howMyoformUsesIt:
        'This insight is read with your energy results to inform fuelling volume and timing guidance.',
      keywords: 'efficiency economy energy cost running economy metabolic',
    },
    {
      id: 'lactate-response',
      category: 'training',
      name: 'Lactate response',
      shortDescription:
        'Genetic tendencies associated with lactate production and clearance.',
      whatItIs:
        'Lactate is produced during hard efforts and recycled as fuel; how quickly it is cleared shapes how repeatable those efforts feel.',
      whyItMatters:
        'Clearance tendencies affect how you should structure interval work and how much rest between efforts is genuinely useful.',
      geneticInfluence:
        'Genetic variants associated with lactate transport and clearance pathways may contribute to differences in threshold characteristics.',
      howMyoformUsesIt:
        'This insight can inform interval guidance and whether buffering support is prioritised in your formula.',
      keywords: 'lactate lactic acid threshold clearance intervals buffering',
    },

    /* ----------------------------------------------------------------- RECOVERY */
    {
      id: 'exercise-induced-inflammation',
      category: 'recovery',
      name: 'Exercise-induced inflammation',
      shortDescription:
        'Genetic differences that may influence inflammatory response following exercise.',
      whatItIs:
        'Hard training creates a short-term inflammatory response that is part of how tissue repairs and adapts.',
      whyItMatters:
        'A stronger or longer response can extend the time you need between hard sessions, even when the training itself was appropriate.',
      geneticInfluence:
        'Variants in inflammatory signalling pathways are associated with differences in the size and duration of the post-exercise response.',
      howMyoformUsesIt:
        'This insight informs recovery spacing guidance and how inflammation-modulating nutrients are timed in your plan.',
      keywords: 'inflammation inflammatory cytokine recovery post exercise',
    },
    {
      id: 'muscle-repair',
      category: 'recovery',
      name: 'Muscle repair',
      shortDescription:
        'Traits associated with muscle recovery and repair after training.',
      whatItIs:
        'Muscle repair is the rebuilding of muscle tissue in the hours and days following a training stimulus.',
      whyItMatters:
        'Repair speed sets the realistic ceiling on training frequency, so it is one of the more practically useful insights.',
      geneticInfluence:
        'Genetic variants associated with tissue repair and remodelling pathways may contribute to differences in recovery rate.',
      howMyoformUsesIt:
        'This insight is read with your protein results to inform post-session nutrition and recovery ingredient priorities.',
      keywords: 'muscle repair recovery regeneration rebuild damage',
    },
    {
      id: 'oxidative-stress',
      category: 'recovery',
      name: 'Oxidative stress',
      shortDescription:
        'How effectively genetic pathways involved in antioxidant defence may operate.',
      whatItIs:
        'Oxidative stress is the balance between reactive by-products of metabolism and the systems that neutralise them.',
      whyItMatters:
        'Persistent imbalance during heavy training blocks is associated with slower recovery and a flatter response to work.',
      geneticInfluence:
        'Variants in antioxidant defence pathways are associated with differences in how efficiently oxidative by-products are cleared.',
      howMyoformUsesIt:
        'This insight helps us decide how much antioxidant support your formula carries and when it is best taken.',
      keywords: 'oxidative stress antioxidant free radical ros glutathione sod',
    },
    {
      id: 'recovery-speed',
      category: 'recovery',
      name: 'Recovery speed',
      shortDescription:
        'Genetic tendencies that may influence how quickly you recover from training stress.',
      whatItIs:
        'A composite view of how quickly you tend to return to full capacity after a demanding session.',
      whyItMatters:
        'Recovery, not training, is usually the limiter on progress, so knowing your tendency helps you set sustainable frequency.',
      geneticInfluence:
        'Genetic variants across inflammatory, repair and sleep-related pathways may collectively contribute to differences in recovery rate.',
      howMyoformUsesIt:
        'This insight shapes recovery guidance in your report and the recovery emphasis in your formula.',
      keywords: 'recovery speed rest days sleep bounce back turnaround',
    },
    {
      id: 'collagen-formation',
      category: 'recovery',
      name: 'Collagen formation',
      shortDescription: 'Genetic factors involved in collagen structure and production.',
      whatItIs:
        'Collagen is the primary structural protein in tendons, ligaments, skin and bone.',
      whyItMatters:
        'Connective tissue adapts more slowly than muscle, which is a common reason load progression outpaces tissue readiness.',
      geneticInfluence:
        'Variants in collagen structural and synthesis pathways are associated with differences in connective tissue characteristics.',
      howMyoformUsesIt:
        'This insight informs connective tissue support in your plan, including cofactors involved in collagen synthesis.',
      keywords: 'collagen connective tissue tendon skin structural protein',
    },
    {
      id: 'tendon-characteristics',
      category: 'recovery',
      name: 'Tendon characteristics',
      shortDescription:
        'Genetic differences associated with tendon structure and resilience.',
      whatItIs:
        'Tendons transmit force from muscle to bone and store elastic energy during movement.',
      whyItMatters:
        'Tendon tolerance often lags behind strength gains, so understanding your tendency supports smarter load progression.',
      geneticInfluence:
        'Genetic variants associated with tendon composition may contribute to differences in stiffness and load tolerance.',
      howMyoformUsesIt:
        'This insight informs load management guidance and connective tissue support priorities in your plan.',
      keywords: 'tendon achilles stiffness elasticity tendinopathy load',
    },
    {
      id: 'ligament-characteristics',
      category: 'recovery',
      name: 'Ligament characteristics',
      shortDescription:
        'Genetic factors associated with connective tissue characteristics.',
      whatItIs:
        'Ligaments stabilise joints by connecting bone to bone and constraining unwanted movement.',
      whyItMatters:
        'Joint stability tendencies are useful context for warm-up, mobility and accessory work choices.',
      geneticInfluence:
        'Variants in connective tissue structural pathways are associated with differences in ligament characteristics.',
      howMyoformUsesIt:
        'This insight contributes to the injury-prevention focus areas suggested in your report.',
      keywords: 'ligament joint stability laxity connective tissue acl',
    },
    {
      id: 'muscle-soreness',
      category: 'recovery',
      name: 'Muscle soreness',
      shortDescription:
        'Genetic tendencies associated with susceptibility to post-exercise muscle soreness.',
      whatItIs:
        'Delayed muscle soreness is the stiffness and tenderness that can follow unfamiliar or high-load training.',
      whyItMatters:
        'Soreness is a poor measure of session quality, so knowing you are simply soreness-prone stops you misreading it as damage.',
      geneticInfluence:
        'Genetic variants associated with inflammatory and repair pathways may contribute to differences in soreness susceptibility.',
      howMyoformUsesIt:
        'This insight informs recovery guidance and the priority given to soreness-supporting nutrients in your formula.',
      keywords: 'soreness doms stiffness ache post exercise pain',
    },
    {
      id: 'training-load-tolerance',
      category: 'recovery',
      name: 'Training-load tolerance',
      shortDescription:
        'How your biology may influence tolerance to repeated physical stress.',
      whatItIs:
        'Load tolerance is how much cumulative training stress you can absorb before performance and recovery start to decline.',
      whyItMatters:
        'It is the practical ceiling on weekly volume, and it differs between people training for identical goals.',
      geneticInfluence:
        'Genetic variants across recovery, inflammatory and stress-response pathways may collectively contribute to differences in load tolerance.',
      howMyoformUsesIt:
        'This insight informs volume guidance in your report and how strongly recovery is weighted in your formula.',
      keywords: 'training load volume tolerance overtraining overreaching capacity',
    },

    /* ------------------------------------------------------------------- ENERGY */
    {
      id: 'fat-oxidation',
      category: 'energy',
      name: 'Fat oxidation',
      shortDescription: 'Your genetic tendency to use fat as a fuel source.',
      whatItIs:
        'Fat oxidation is the process of breaking down fat to produce energy, and it dominates at lower intensities.',
      whyItMatters:
        'A stronger fat-burning tendency can support longer efforts on less external fuel, which changes race and session nutrition.',
      geneticInfluence:
        'Genetic variants in fat transport and oxidation pathways may contribute to differences in fuel preference during exercise.',
      howMyoformUsesIt:
        'This insight informs fuelling guidance and the balance of fat and carbohydrate support in your plan.',
      keywords: 'fat oxidation fat burning fuel substrate lipolysis',
    },
    {
      id: 'carbohydrate-utilisation',
      category: 'energy',
      name: 'Carbohydrate utilisation',
      shortDescription:
        'How your body may process and utilise carbohydrates during activity.',
      whatItIs:
        'Carbohydrate utilisation covers how readily stored and ingested carbohydrate is converted into usable energy during effort.',
      whyItMatters:
        'Carbohydrate is the dominant fuel at higher intensities, so utilisation tendencies directly affect intra-session fuelling.',
      geneticInfluence:
        'Variants in glucose transport and carbohydrate metabolism pathways are associated with differences in utilisation during exercise.',
      howMyoformUsesIt:
        'This insight informs the carbohydrate type, timing and quantity suggested around your sessions.',
      keywords: 'carbohydrate utilisation glycogen glucose fuel intra workout',
    },
    {
      id: 'mitochondrial-function',
      category: 'energy',
      name: 'Mitochondrial function',
      shortDescription: 'Genetic pathways involved in cellular energy production.',
      whatItIs:
        'Mitochondria convert fuel and oxygen into the chemical energy your cells run on.',
      whyItMatters:
        'Mitochondrial capacity underpins both endurance performance and everyday energy, and it responds well to the right training.',
      geneticInfluence:
        'Genetic variants in mitochondrial biogenesis and function pathways may contribute to differences in cellular energy production.',
      howMyoformUsesIt:
        'This insight informs which cofactors involved in energy production are prioritised in your formula.',
      keywords: 'mitochondria mitochondrial atp cellular energy biogenesis',
    },
    {
      id: 'metabolic-efficiency',
      category: 'energy',
      name: 'Metabolic efficiency',
      shortDescription:
        'Traits associated with how efficiently your body creates and uses energy.',
      whatItIs:
        'Metabolic efficiency describes how well your body converts available fuel into usable energy with minimal waste.',
      whyItMatters:
        'Efficiency tendencies influence both performance at a given intensity and how energy feels across the day.',
      geneticInfluence:
        'Variants across metabolic and mitochondrial pathways are associated with differences in energy conversion efficiency.',
      howMyoformUsesIt:
        'This insight is read with your fuel and mitochondrial results to shape energy support in your plan.',
      keywords: 'metabolic efficiency metabolism energy conversion economy',
    },
    {
      id: 'energy-availability',
      category: 'energy',
      name: 'Energy availability',
      shortDescription:
        'Genetic factors involved in pathways associated with day-to-day energy production.',
      whatItIs:
        'Energy availability describes how readily fuel is released and used for training and daily demands.',
      whyItMatters:
        'Day-to-day energy is where most people actually notice their metabolic profile, well before it shows in performance data.',
      geneticInfluence:
        'Genetic variants in energy metabolism pathways may contribute to differences in how consistently energy is produced.',
      howMyoformUsesIt:
        'This insight informs which energy-metabolism cofactors are prioritised and how they are timed across your day.',
      keywords: 'energy availability daily energy tiredness fatigue metabolism',
    },
    {
      id: 'exercise-fuel-response',
      category: 'energy',
      name: 'Exercise fuel response',
      shortDescription:
        'How your biology may influence fuel utilisation during training.',
      whatItIs:
        'Fuel response is the way your body shifts between fat and carbohydrate as intensity and duration change.',
      whyItMatters:
        'Where that crossover sits determines when you need to start fuelling, and how much.',
      geneticInfluence:
        'Variants in substrate metabolism pathways are associated with differences in fuel selection during exercise.',
      howMyoformUsesIt:
        'This insight informs intra-session fuelling guidance and the fuel-support ingredients in your formula.',
      keywords: 'fuel response substrate crossover fuelling intra workout',
    },
    {
      id: 'glucose-response',
      category: 'energy',
      name: 'Glucose response',
      shortDescription: 'Genetic tendencies associated with glucose regulation.',
      whatItIs:
        'Glucose response describes how blood sugar moves after eating and how quickly it returns to baseline.',
      whyItMatters:
        'Glucose stability influences energy, focus and appetite across the day, not only performance.',
      geneticInfluence:
        'Genetic variants associated with glucose regulation pathways may contribute to differences in post-meal response.',
      howMyoformUsesIt:
        'This insight informs meal composition and carbohydrate timing guidance in your plan.',
      keywords: 'glucose blood sugar glycaemic response post meal spike',
    },
    {
      id: 'insulin-sensitivity',
      category: 'energy',
      name: 'Insulin sensitivity',
      shortDescription:
        'Genetic factors associated with insulin signalling and glucose metabolism.',
      whatItIs:
        'Insulin sensitivity reflects how readily your cells respond to insulin and take up glucose from the blood.',
      whyItMatters:
        'It is strongly influenced by training, sleep and body composition, so a genetic tendency is a starting point rather than a fixed state.',
      geneticInfluence:
        'Variants in insulin signalling pathways are associated with differences in glucose handling between individuals.',
      howMyoformUsesIt:
        'This insight is read with your glucose results to inform carbohydrate strategy and training emphasis. It is not a medical assessment.',
      keywords: 'insulin sensitivity resistance glucose metabolic health',
    },

    /* ---------------------------------------------------------- NEUROPERFORMANCE */
    {
      id: 'stress-response',
      category: 'neuroperformance',
      name: 'Stress response',
      shortDescription:
        'How strongly your body may respond to physiological and psychological stress.',
      whatItIs:
        'Your stress response is the hormonal and neurological cascade triggered by physical or mental demand.',
      whyItMatters:
        'Training is a stressor, so life stress and training stress draw on the same recovery budget.',
      geneticInfluence:
        'Genetic variants in stress hormone and signalling pathways may contribute to differences in reactivity and return to baseline.',
      howMyoformUsesIt:
        'This insight informs recovery guidance and whether stress-resilience support is prioritised in your formula.',
      keywords: 'stress cortisol hpa axis resilience anxiety sleep recovery',
    },
    {
      id: 'sleep-characteristics',
      category: 'neuroperformance',
      name: 'Sleep characteristics',
      shortDescription:
        'Genetic tendencies associated with sleep behaviour and recovery.',
      whatItIs:
        'Sleep characteristics cover tendencies in sleep duration, depth and how restorative sleep tends to feel.',
      whyItMatters:
        'Sleep is the single largest recovery input, so tendencies here affect almost every other result in your report.',
      geneticInfluence:
        'Genetic variants associated with sleep regulation pathways may contribute to differences in sleep duration and quality.',
      howMyoformUsesIt:
        'This insight informs recovery guidance and the timing of any sleep-supporting nutrients in your plan.',
      keywords: 'sleep quality duration deep sleep insomnia rest recovery',
    },
    {
      id: 'circadian-rhythm',
      category: 'neuroperformance',
      name: 'Circadian rhythm',
      shortDescription:
        'Your genetic predisposition towards different sleep and wake patterns.',
      whatItIs:
        'Your circadian rhythm is the internal clock that sets your natural sleep, wake and alertness pattern.',
      whyItMatters:
        'Training and eating against your natural rhythm makes consistency harder than it needs to be.',
      geneticInfluence:
        'Variants in circadian clock pathways are associated with differences in chronotype and preferred timing.',
      howMyoformUsesIt:
        'This insight informs session timing guidance and when supplements are best taken across your day.',
      keywords: 'circadian rhythm chronotype sleep wake morning evening body clock',
    },
    {
      id: 'focus-and-attention',
      category: 'neuroperformance',
      name: 'Focus and attention',
      shortDescription:
        'Traits associated with neurological pathways involved in concentration.',
      whatItIs:
        'Focus and attention describe the neurological pathways that sustain concentration on a task.',
      whyItMatters:
        'Attention quality affects training execution and technical work, not just desk performance.',
      geneticInfluence:
        'Genetic variants in neurotransmitter pathways associated with attention may contribute to differences in sustained concentration.',
      howMyoformUsesIt:
        'This insight informs whether cognitive-support ingredients are prioritised, and how stimulants are dosed given your caffeine profile.',
      keywords: 'focus attention concentration cognitive nootropic clarity',
    },
    {
      id: 'motivation-and-reward',
      category: 'neuroperformance',
      name: 'Motivation and reward',
      shortDescription:
        'Genetic differences within pathways associated with motivation and reward response.',
      whatItIs:
        'Reward pathways shape how rewarding effort feels and how readily you initiate hard work.',
      whyItMatters:
        'Adherence beats optimisation, so understanding what keeps you engaged is genuinely performance-relevant.',
      geneticInfluence:
        'Variants in reward signalling pathways are associated with differences in motivation and reward sensitivity.',
      howMyoformUsesIt:
        'This insight informs the behavioural and habit guidance in your report alongside neurological support in your formula.',
      keywords: 'motivation reward drive dopamine adherence habit discipline',
    },
    {
      id: 'cognitive-performance-under-stress',
      category: 'neuroperformance',
      name: 'Cognitive performance under stress',
      shortDescription:
        'How neurological response may differ during periods of elevated stress.',
      whatItIs:
        'This covers how well decision-making and concentration hold up when physical or mental load is high.',
      whyItMatters:
        'Most performance that matters happens under fatigue, so how you think when tired is the useful measure.',
      geneticInfluence:
        'Genetic variants in stress and neurotransmitter pathways may contribute to differences in cognitive performance under load.',
      howMyoformUsesIt:
        'This insight informs stress-resilience and cognitive support priorities in your plan.',
      keywords: 'cognitive stress pressure decision making fatigue mental performance',
    },
    {
      id: 'dopamine-signalling',
      category: 'neuroperformance',
      name: 'Dopamine signalling',
      shortDescription: 'Genetic factors involved in dopamine-related pathways.',
      whatItIs:
        'Dopamine pathways contribute to motivation, movement control, focus and reward processing.',
      whyItMatters:
        'Dopamine signalling links motivation and motor control, which is why it shows up in both training drive and technical skill.',
      geneticInfluence:
        'Variants associated with dopamine production, transport and receptor pathways may contribute to differences in signalling.',
      howMyoformUsesIt:
        'This insight informs which precursor nutrients and cofactors are considered for cognitive support in your formula.',
      keywords: 'dopamine neurotransmitter motivation tyrosine comt reward',
    },
    {
      id: 'serotonin-pathways',
      category: 'neuroperformance',
      name: 'Serotonin pathways',
      shortDescription:
        'Genetic differences in pathways related to serotonin signalling.',
      whatItIs:
        'Serotonin pathways contribute to mood regulation, sleep onset, appetite and perceived effort.',
      whyItMatters:
        'Serotonin sits between sleep, mood and appetite, so tendencies here often explain patterns across all three.',
      geneticInfluence:
        'Genetic variants in serotonin transport and receptor pathways are associated with differences in signalling.',
      howMyoformUsesIt:
        'This insight informs sleep and mood-adjacent support in your plan. It is not a mental health assessment.',
      keywords: 'serotonin mood sleep tryptophan 5-htp appetite',
    },

    /* ---------------------------------------------------------------- HYDRATION */
    {
      id: 'sodium-sensitivity',
      category: 'hydration',
      name: 'Sodium sensitivity',
      shortDescription:
        'Genetic tendencies associated with your response to dietary sodium.',
      whatItIs:
        'Sodium sensitivity describes how strongly your body responds to changes in sodium intake.',
      whyItMatters:
        'Athletes often need more sodium than general guidance suggests, and sensitivity determines how far that should go.',
      geneticInfluence:
        'Genetic variants in sodium handling pathways may contribute to differences in response to dietary sodium.',
      howMyoformUsesIt:
        'This insight informs electrolyte guidance and the sodium content of any hydration support in your plan.',
      keywords: 'sodium salt sensitivity electrolyte intake retention',
    },
    {
      id: 'fluid-regulation',
      category: 'hydration',
      name: 'Fluid regulation',
      shortDescription: 'Genetic factors involved in maintaining fluid balance.',
      whatItIs:
        'Fluid regulation is the hormonal and kidney-driven process that keeps your body water in balance.',
      whyItMatters:
        'Small fluid deficits are associated with measurable drops in performance and perceived effort.',
      geneticInfluence:
        'Variants in fluid balance and hormonal regulation pathways are associated with differences in fluid handling.',
      howMyoformUsesIt:
        'This insight informs daily fluid guidance and how hydration is structured around your sessions.',
      keywords: 'fluid balance hydration water retention kidney vasopressin',
    },
    {
      id: 'electrolyte-balance',
      category: 'hydration',
      name: 'Electrolyte balance',
      shortDescription: 'Genetic differences associated with electrolyte handling.',
      whatItIs:
        'Electrolytes carry the electrical signals behind muscle contraction, nerve function and fluid distribution.',
      whyItMatters:
        'Drinking more water without replacing electrolytes can dilute rather than solve a hydration problem.',
      geneticInfluence:
        'Genetic variants in electrolyte transport pathways may contribute to differences in balance and requirement.',
      howMyoformUsesIt:
        'This insight informs the electrolyte profile and dosing of hydration support in your formula.',
      keywords: 'electrolyte sodium potassium magnesium balance cramp',
    },
    {
      id: 'sweat-characteristics',
      category: 'hydration',
      name: 'Sweat characteristics',
      shortDescription:
        'Genetic tendencies that may influence sweat-related characteristics.',
      whatItIs:
        'Sweat characteristics cover tendencies in sweat rate and the concentration of electrolytes lost through sweat.',
      whyItMatters:
        'A heavy or salty sweat profile changes replacement needs substantially, especially in heat or long sessions.',
      geneticInfluence:
        'Genetic variants associated with sweat gland function and electrolyte transport may contribute to differences in sweat composition.',
      howMyoformUsesIt:
        'This insight informs sweat replacement guidance and the electrolyte concentration suggested during training.',
      keywords: 'sweat rate salty sweater perspiration heat sodium loss',
    },
    {
      id: 'hydration-response',
      category: 'hydration',
      name: 'Hydration response',
      shortDescription:
        'Traits associated with the body’s response to changes in hydration.',
      whatItIs:
        'Hydration response describes how your body reacts to becoming dehydrated and to rehydrating afterwards.',
      whyItMatters:
        'Some people feel performance effects from small deficits, which is worth knowing before a hot or long session.',
      geneticInfluence:
        'Variants across fluid and electrolyte regulation pathways may collectively contribute to differences in hydration response.',
      howMyoformUsesIt:
        'This insight informs pre-, intra- and post-session hydration guidance in your plan.',
      keywords: 'hydration response dehydration rehydration thirst fluid',
    },
    {
      id: 'blood-pressure-response-to-sodium',
      category: 'hydration',
      name: 'Blood-pressure response to sodium',
      shortDescription:
        'Genetic differences associated with blood-pressure response to sodium intake.',
      whatItIs:
        'This describes how your blood pressure tends to respond to changes in dietary sodium.',
      whyItMatters:
        'It is the sensible upper guardrail on the higher sodium intakes that endurance training can otherwise justify.',
      geneticInfluence:
        'Genetic variants in sodium handling and blood pressure regulation pathways are associated with differences in this response.',
      howMyoformUsesIt:
        'This insight acts as a ceiling on sodium recommendations in your plan. It is not a medical assessment - discuss blood pressure with your clinician.',
      keywords: 'blood pressure sodium salt hypertension cardiovascular',
    },

    /* ------------------------------------------------------------- SENSITIVITIES */
    {
      id: 'caffeine-metabolism',
      category: 'sensitivities',
      name: 'Caffeine metabolism',
      shortDescription: 'How quickly your body may break down caffeine.',
      whatItIs:
        'Caffeine metabolism describes the rate at which caffeine is cleared from your system after you consume it.',
      whyItMatters:
        'A slower clearance tendency means an afternoon coffee can still be active at bedtime, quietly costing you recovery.',
      geneticInfluence:
        'Genetic variants in caffeine clearance pathways are associated with meaningful differences in how long caffeine remains active.',
      howMyoformUsesIt:
        'This insight informs caffeine dosing and, more importantly, the latest sensible timing before sleep.',
      keywords: 'caffeine metabolism coffee clearance fast slow metaboliser cyp1a2',
    },
    {
      id: 'caffeine-sensitivity',
      category: 'sensitivities',
      name: 'Caffeine sensitivity',
      shortDescription: 'How strongly you may respond to caffeine.',
      whatItIs:
        'Sensitivity describes the strength of your subjective and physiological response to a given dose of caffeine.',
      whyItMatters:
        'Sensitivity and clearance are separate traits, which is why some people feel a lot from a little and others feel very little.',
      geneticInfluence:
        'Variants in adenosine receptor pathways are associated with differences in the perceived effects of caffeine.',
      howMyoformUsesIt:
        'Read with your caffeine metabolism result, this insight informs whether stimulants belong in your formula and at what dose.',
      keywords: 'caffeine sensitivity jitters anxiety stimulant response adenosine',
    },
    {
      id: 'alcohol-metabolism',
      category: 'sensitivities',
      name: 'Alcohol metabolism',
      shortDescription: 'Genetic differences affecting how alcohol is processed.',
      whatItIs:
        'Alcohol metabolism is the two-step enzymatic process that breaks alcohol down and clears its by-products.',
      whyItMatters:
        'Clearance tendencies affect how much alcohol interferes with the sleep and recovery that follow.',
      geneticInfluence:
        'Genetic variants in alcohol-processing enzyme pathways are associated with differences in clearance rate and tolerance.',
      howMyoformUsesIt:
        'This insight provides context for recovery guidance around social and travel weeks.',
      keywords: 'alcohol metabolism flush tolerance hangover acetaldehyde',
    },
    {
      id: 'histamine-response',
      category: 'sensitivities',
      name: 'Histamine response',
      shortDescription:
        'Variants within pathways involved in histamine breakdown.',
      whatItIs:
        'Histamine is a signalling molecule found in some foods and released by the body, and it is cleared by dedicated enzyme pathways.',
      whyItMatters:
        'Slower clearance tendencies can help explain reactions to aged, fermented or leftover foods that appear inconsistent.',
      geneticInfluence:
        'Genetic variants in histamine breakdown pathways may contribute to differences in histamine clearance.',
      howMyoformUsesIt:
        'This insight provides context for dietary guidance and cofactor support in your plan. It is not an allergy test.',
      keywords: 'histamine intolerance dao fermented aged food reaction',
    },
    {
      id: 'lactose-tolerance',
      category: 'sensitivities',
      name: 'Lactose tolerance',
      shortDescription:
        'Genetic tendencies associated with the ability to digest lactose.',
      whatItIs:
        'Lactose tolerance depends on whether the enzyme that digests milk sugar stays active into adulthood.',
      whyItMatters:
        'This is one of the clearest genetic traits in nutrition, and it directly affects whether dairy-based protein suits you.',
      geneticInfluence:
        'Genetic variants associated with lactase persistence are strongly linked to the ability to digest lactose in adulthood.',
      howMyoformUsesIt:
        'This insight informs whether dairy-based or plant-based protein is recommended in your plan.',
      keywords: 'lactose dairy milk lactase intolerance whey digestion',
    },
    {
      id: 'gluten-related-genetics',
      category: 'sensitivities',
      name: 'Gluten-related genetics',
      shortDescription:
        'Genetic variants associated with biological responses related to gluten.',
      whatItIs:
        'Certain immune-related genetic variants are associated with how the body responds to gluten-containing foods.',
      whyItMatters:
        'Because most people carry no relevant variants, this insight is often more useful for ruling a concern out than in.',
      geneticInfluence:
        'Variants in immune signalling pathways are associated with differences in gluten-related biological response.',
      howMyoformUsesIt:
        'This insight provides context for dietary guidance only. It is not a diagnosis, and coeliac disease can only be diagnosed by a clinician.',
      keywords: 'gluten wheat coeliac celiac hla gliadin sensitivity',
    },
    {
      id: 'food-sensitivities',
      category: 'sensitivities',
      name: 'Food sensitivities',
      shortDescription:
        'Genetic tendencies that may influence responses to certain dietary compounds.',
      whatItIs:
        'This covers genetic tendencies in how specific dietary compounds are processed and tolerated.',
      whyItMatters:
        'It gives you a shorter, more informed list to test against, rather than eliminating foods at random.',
      geneticInfluence:
        'Genetic variants in a range of metabolic and immune pathways may contribute to differences in how dietary compounds are tolerated.',
      howMyoformUsesIt:
        'This insight informs dietary guidance in your report. It is not an allergy or intolerance test.',
      keywords: 'food sensitivity intolerance dietary compounds reaction elimination',
    },
    {
      id: 'supplement-response',
      category: 'sensitivities',
      name: 'Supplement response',
      shortDescription:
        'Genetic factors that may influence how your body responds to particular nutrients or performance ingredients.',
      whatItIs:
        'Supplement response describes how strongly you may respond to specific nutrients and performance ingredients.',
      whyItMatters:
        'It is the difference between a formula that works for you and one that works on average - and the reason Myoform exists.',
      geneticInfluence:
        'Genetic variants across metabolism, transport and receptor pathways may contribute to differences in individual supplement response.',
      howMyoformUsesIt:
        'This insight sits at the centre of your formulation, informing which ingredients are included, at what dose and in what form.',
      keywords: 'supplement response responder creatine beta alanine nutrient personalised',
    },
  ],
};
